import { useEffect, useRef, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { matchShotsQueryOptions, matchKpisQueryOptions, Shot } from "@/api/ranking";
import { tournamentsQueryOptions } from "@/api/tournaments";
import { Loader } from "../../common/loader";
import { useSelectedTeam } from "@/context/teamContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Clock, User } from "lucide-react";
import { getTeamLogo } from "@/lib/utils";
import dayjs from "dayjs";

interface ShotHeatMapProps {
    className?: string;
    matchIds?: number[];
    defaultTeamFilter?: string;
    isLoading?: boolean;
}

interface PlayerKPI {
    playerId: number;
    pointsOffensive: number;
    pointsDefensive: number;
    pointsTeamPlay: number;
    pointsEffort: number;
    pointsBonus: number;
    totalPoints: number;
}

interface ShotWithMatch extends Shot {
    matchId?: number;
}

export function ShotHeatMap({
    className = "",
    matchIds: propMatchIds,
    isLoading: externalLoading = false,
}: ShotHeatMapProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<string>("all");
    const [selectedResultFilter, setSelectedResultFilter] = useState<string>("all");
    const [selectedPlayerFilter, setSelectedPlayerFilter] = useState<string>("all");
    const [hoveredShot, setHoveredShot] = useState<ShotWithMatch | null>(null);
    const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

    const { teamId } = useSelectedTeam();
    const effectiveMatchIds = propMatchIds || [];

    const { data: shots, isLoading: shotsLoading } = useQuery(
        matchShotsQueryOptions(effectiveMatchIds.length > 0 ? effectiveMatchIds : undefined, teamId)
    );
    const { data: kpiData, isLoading: kpiLoading } = useQuery(
        matchKpisQueryOptions(effectiveMatchIds.length > 0 ? effectiveMatchIds : undefined, teamId)
    );
    const { data: tournaments } = useQuery(tournamentsQueryOptions(teamId));

    const isLoading = externalLoading || shotsLoading || kpiLoading;

    const matchLookup = useMemo(() => {
        if (!tournaments) return {};

        const lookup: Record<
            number,
            {
                homeTeam: string;
                awayTeam: string;
                homeGoals: number;
                awayGoals: number;
                date: string;
                tournamentName: string;
            }
        > = {};

        tournaments.forEach((tournament) => {
            tournament.matches.forEach((match) => {
                lookup[match.id] = {
                    homeTeam: match.homeTeam.shortName,
                    awayTeam: match.awayTeam.shortName,
                    homeGoals: match.homeGoals,
                    awayGoals: match.awayGoals,
                    date: match.date ?? "",
                    tournamentName: tournament.name,
                };
            });
        });

        return lookup;
    }, [tournaments]);

    const playerLookup =
        kpiData?.average?.reduce(
            (acc: Record<number, string>, player) => {
                if (player.playerId) {
                    acc[player.playerId] = `${player.firstName} ${player.lastName}`;
                }
                return acc;
            },
            {} as Record<number, string>
        ) || {};

    const playerKpiLookup =
        kpiData?.average?.reduce(
            (acc: Record<number, PlayerKPI>, player) => {
                if (player.playerId) {
                    acc[player.playerId] = {
                        playerId: player.playerId,
                        pointsOffensive: player.pointsOffensive || 0,
                        pointsDefensive: player.pointsDefensive || 0,
                        pointsTeamPlay: player.pointsTeamPlay || 0,
                        pointsEffort: player.pointsEffort || 0,
                        pointsBonus: player.pointsBonus || 0,
                        totalPoints: player.totalPoints || 0,
                    };
                }
                return acc;
            },
            {} as Record<number, PlayerKPI>
        ) || {};

    const valerengaPlayerIds = kpiData?.average?.map((player) => player.playerId)?.filter(Boolean) || [];

    const shotPlayerIds = Array.from(new Set(shots?.map((shot) => shot.shooterId).filter(Boolean))) || [];
    const availablePlayers = (() => {
        const seen = new Set<number>();
        return (
            kpiData?.average
                ?.filter((player) => {
                    if (!player.playerId || !shotPlayerIds.includes(player.playerId)) return false;
                    if (seen.has(player.playerId)) return false;
                    seen.add(player.playerId);
                    return true;
                })
                .map((player) => ({
                    id: player.playerId!,
                    name: `${player.firstName} ${player.lastName}`,
                    teamId: 527810,
                })) || []
        );
    })();

    const availablePeriods = Array.from(new Set(shots?.map((s) => s.period) ?? [])).sort((a, b) => a - b);
    const periodLabel = (p: number) =>
        p === 1 ? "1st Period" : p === 2 ? "2nd Period" : p === 3 ? "3rd Period" : "Overtime";

    const baseFilteredShots =
        shots?.filter((shot) => {
            const periodMatch = selectedPeriod === "all" || shot.period.toString() === selectedPeriod;
            const isValerengaPlayer = shot.shooterId && valerengaPlayerIds.includes(shot.shooterId);
            const playerMatch = selectedPlayerFilter === "all" || shot.shooterId?.toString() === selectedPlayerFilter;
            return periodMatch && isValerengaPlayer && playerMatch;
        }) || [];

    const filteredShots =
        selectedResultFilter === "all"
            ? baseFilteredShots
            : baseFilteredShots.filter((shot) => {
                  const result = shot.result.toLowerCase();
                  if (selectedResultFilter === "goals") return result.includes("goal");
                  if (selectedResultFilter === "saved") return result.includes("saved");
                  if (selectedResultFilter === "blocked") return result.includes("blocked");
                  if (selectedResultFilter === "missed") return result.includes("missed");
                  return true;
              });

    useEffect(() => {
        if (!shots || shots.length === 0) {
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.globalCompositeOperation = "lighter";

        const canvasWidth = rect.width;
        const canvasHeight = rect.height;
        const scaleFactorX = canvasWidth / 920;
        const scaleFactorY = canvasHeight / 320;

        const scaleX = (x: number) => (460 + x * 15) * scaleFactorX;
        const scaleY = (y: number) => (160 - y * 10) * scaleFactorY;

        const drawShotPoint = (shot: ShotWithMatch) => {
            const x = scaleX(shot.startXPosition);
            const y = scaleY(shot.startYPosition);

            let color, strokeColor, radius;
            const result = shot.result.toLowerCase();
            const scaleFactor = Math.min(scaleFactorX, scaleFactorY);

            if (result.includes("goal")) {
                color = "#EF4444";
                strokeColor = "#FFFFFF";
                radius = 8 * scaleFactor;
            } else if (result.includes("saved")) {
                color = "#3B82F6";
                strokeColor = "#1E40AF";
                radius = 6 * scaleFactor;
            } else if (result.includes("blocked")) {
                color = "#F59E0B";
                strokeColor = "#D97706";
                radius = 6 * scaleFactor;
            } else if (result.includes("missed")) {
                color = "#6B7280";
                strokeColor = "#374151";
                radius = 6 * scaleFactor;
            } else {
                color = "#60A5FA";
                strokeColor = "#2563EB";
                radius = 6 * scaleFactor;
            }

            ctx.beginPath();

            if (result.includes("blocked")) {
                ctx.moveTo(x, y - radius);
                ctx.lineTo(x - radius, y + radius);
                ctx.lineTo(x + radius, y + radius);
                ctx.closePath();
            } else {
                ctx.arc(x, y, radius, 0, 2 * Math.PI);
            }
            ctx.fillStyle = color;
            ctx.fill();

            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 2 * Math.min(scaleFactorX, scaleFactorY);
            ctx.stroke();

            if (result.includes("missed")) {
                const crossSize = 4 * Math.min(scaleFactorX, scaleFactorY);
                ctx.beginPath();
                ctx.moveTo(x - crossSize, y - crossSize);
                ctx.lineTo(x + crossSize, y + crossSize);
                ctx.moveTo(x + crossSize, y - crossSize);
                ctx.lineTo(x - crossSize, y + crossSize);
                ctx.strokeStyle = "#FFFFFF";
                ctx.lineWidth = 2 * Math.min(scaleFactorX, scaleFactorY);
                ctx.stroke();
            }

            if (shot.shooterId && playerKpiLookup[shot.shooterId]) {
                const kpi = playerKpiLookup[shot.shooterId];
                const indicatorRadius = 3 * scaleFactor;
                const indicatorDistance = radius + 5 * scaleFactor;

                const offensiveRatio = Math.min(kpi.pointsOffensive / 20, 1);
                const defensiveRatio = Math.min(kpi.pointsDefensive / 20, 1);
                const teamPlayRatio = Math.min(kpi.pointsTeamPlay / 20, 1);

                if (offensiveRatio > 0.1) {
                    ctx.beginPath();
                    ctx.arc(x, y - indicatorDistance, indicatorRadius * offensiveRatio, 0, 2 * Math.PI);
                    ctx.fillStyle = `rgba(239, 68, 68, ${0.5 + offensiveRatio * 0.5})`;
                    ctx.fill();
                    ctx.strokeStyle = "#EF4444";
                    ctx.lineWidth = 1 * scaleFactor;
                    ctx.stroke();
                }

                if (defensiveRatio > 0.1) {
                    const angle = Math.PI * 0.75;
                    const dx = Math.cos(angle) * indicatorDistance;
                    const dy = Math.sin(angle) * indicatorDistance;
                    ctx.beginPath();
                    ctx.arc(x + dx, y + dy, indicatorRadius * defensiveRatio, 0, 2 * Math.PI);
                    ctx.fillStyle = `rgba(59, 130, 246, ${0.5 + defensiveRatio * 0.5})`;
                    ctx.fill();
                    ctx.strokeStyle = "#3B82F6";
                    ctx.lineWidth = 1 * scaleFactor;
                    ctx.stroke();
                }

                if (teamPlayRatio > 0.1) {
                    const angle = Math.PI * 0.25;
                    const dx = Math.cos(angle) * indicatorDistance;
                    const dy = Math.sin(angle) * indicatorDistance;
                    ctx.beginPath();
                    ctx.arc(x + dx, y + dy, indicatorRadius * teamPlayRatio, 0, 2 * Math.PI);
                    ctx.fillStyle = `rgba(34, 197, 94, ${0.5 + teamPlayRatio * 0.5})`;
                    ctx.fill();
                    ctx.strokeStyle = "#22C55E";
                    ctx.lineWidth = 1 * scaleFactor;
                    ctx.stroke();
                }
            }
        };

        filteredShots.forEach(drawShotPoint);
    }, [filteredShots, playerKpiLookup]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resizeObserver = new ResizeObserver(() => {
            if (filteredShots.length > 0) {
                const event = new Event("resize");
                window.dispatchEvent(event);
            }
        });

        resizeObserver.observe(canvas.parentElement!);

        return () => {
            resizeObserver.disconnect();
        };
    }, [filteredShots]);

    const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        let foundShot: ShotWithMatch | null = null;
        const scaleFactorX = rect.width / 920;
        const scaleFactorY = rect.height / 320;

        for (const shot of filteredShots) {
            const shotX = (460 + shot.startXPosition * 15) * scaleFactorX;
            const shotY = (160 - shot.startYPosition * 10) * scaleFactorY;

            const distance = Math.sqrt(Math.pow(mouseX - shotX, 2) + Math.pow(mouseY - shotY, 2));

            if (distance <= 12 * Math.min(scaleFactorX, scaleFactorY)) {
                foundShot = shot;
                break;
            }
        }

        setHoveredShot(foundShot);
        if (foundShot) {
            const containerRect = canvas.parentElement?.getBoundingClientRect();
            if (containerRect) {
                setMousePosition({
                    x: event.clientX - containerRect.left,
                    y: event.clientY - containerRect.top,
                });
            }
        } else {
            setMousePosition(null);
        }
    };

    const handleMouseLeave = () => {
        setHoveredShot(null);
        setMousePosition(null);
    };

    const totalBaseShots = baseFilteredShots.length;
    const baseGoals = baseFilteredShots.filter((s) => s.result.toLowerCase().includes("goal")).length;
    const baseSaved = baseFilteredShots.filter((s) => s.result.toLowerCase().includes("saved")).length;
    const baseBlocked = baseFilteredShots.filter((s) => s.result.toLowerCase().includes("blocked")).length;
    const baseMissed = baseFilteredShots.filter((s) => s.result.toLowerCase().includes("missed")).length;

    if (isLoading) {
        return (
            <div className={`h-[600px] min-h-0 flex items-center justify-center ${className}`}>
                <Loader message="Loading shot chart data..." />
            </div>
        );
    }

    if (!shots || shots.length === 0) {
        return (
            <div className={`h-[600px] min-h-0 flex items-center justify-center ${className}`}>
                <div className="text-on-surface-variant">No shot data available for the selected matches</div>
            </div>
        );
    }

    return (
        <div className={className}>
            <div className="flex flex-wrap items-center justify-end gap-2">
                <div className="flex items-center gap-2 flex-1">
                    <h3 className="text-lg font-headline font-bold section-title whitespace-nowrap">Shot Chart</h3>
                    {effectiveMatchIds.length > 1 && (
                        <span className="text-sm text-on-surface-variant">({effectiveMatchIds.length} matches)</span>
                    )}
                </div>
                <nav
                    id="onboarding-heatmap-result-filters"
                    className="rounded-md p-1 bg-surface-container-low w-full sm:w-auto shrink-0"
                >
                    <div className="grid grid-cols-3 gap-1 sm:flex sm:items-center sm:gap-0">
                        <button
                            onClick={() => setSelectedResultFilter("all")}
                            className={`nav-tab ${selectedResultFilter === "all" ? "nav-tab-active" : "nav-tab-inactive"}`}
                        >
                            All · {totalBaseShots}
                        </button>
                        <button
                            onClick={() => setSelectedResultFilter("goals")}
                            className={`nav-tab ${selectedResultFilter === "goals" ? "nav-tab-active" : "nav-tab-inactive"}`}
                        >
                            <span className="flex items-center justify-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                                Goals · {baseGoals}
                            </span>
                        </button>
                        <button
                            onClick={() => setSelectedResultFilter("saved")}
                            className={`nav-tab ${selectedResultFilter === "saved" ? "nav-tab-active" : "nav-tab-inactive"}`}
                        >
                            <span className="flex items-center justify-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                                Saved · {baseSaved}
                            </span>
                        </button>
                        <button
                            onClick={() => setSelectedResultFilter("blocked")}
                            className={`nav-tab ${selectedResultFilter === "blocked" ? "nav-tab-active" : "nav-tab-inactive"}`}
                        >
                            <span className="flex items-center justify-center gap-1.5">
                                <span
                                    className="w-2 h-2 bg-orange-500 shrink-0"
                                    style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }}
                                />
                                Blocked · {baseBlocked}
                            </span>
                        </button>
                        <button
                            onClick={() => setSelectedResultFilter("missed")}
                            className={`nav-tab ${selectedResultFilter === "missed" ? "nav-tab-active" : "nav-tab-inactive"}`}
                        >
                            <span className="flex items-center justify-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-surface-container-highest border border-outline-variant shrink-0" />
                                Missed · {baseMissed}
                            </span>
                        </button>
                    </div>
                </nav>
                <div id="onboarding-heatmap-filters" className="flex items-center gap-2 w-full sm:w-auto">
                    <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                        <SelectTrigger className="h-auto py-1 w-fit bg-surface-container-highest border-0 text-xs shrink-0">
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                                <SelectValue />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Periods</SelectItem>
                            {availablePeriods.map((p) => (
                                <SelectItem key={p} value={p.toString()}>
                                    {periodLabel(p)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={selectedPlayerFilter} onValueChange={setSelectedPlayerFilter}>
                        <SelectTrigger className="h-auto py-1 bg-surface-container-highest border-0 text-xs flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                                <User className="w-3.5 h-3.5 text-primary shrink-0" />
                                <SelectValue className="truncate" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Players</SelectItem>
                            {availablePlayers.map((player) => (
                                <SelectItem key={player.id} value={player.id.toString()}>
                                    {player.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div id="onboarding-heatmap-chart" className="relative w-full my-6 sm:px-14">
                <div className="relative overflow-hidden w-full" style={{ paddingBottom: "34.78%" }}>
                    <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox="70 30 920 320"
                        preserveAspectRatio="xMidYMid meet"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <defs>
                            <linearGradient id="rinkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style={{ stopColor: "#1e3a8a", stopOpacity: 0.1 }} />
                                <stop offset="100%" style={{ stopColor: "#1e40af", stopOpacity: 0.2 }} />
                            </linearGradient>
                        </defs>

                        <rect
                            x="80"
                            y="40"
                            width="900"
                            height="300"
                            fill="url(#rinkGradient)"
                            stroke="#fff"
                            strokeWidth="3"
                            rx="120"
                        />

                        <line x1="530" y1="40" x2="530" y2="340" stroke="#ef4444" strokeWidth="3" opacity="0.8" />

                        <circle
                            cx="530"
                            cy="190"
                            r="67"
                            fill="none"
                            stroke="var(--color-chart-blue)"
                            strokeWidth="2"
                            opacity="0.7"
                        />
                        <circle cx="530" cy="190" r="5" fill="var(--color-chart-blue)" opacity="0.8" />

                        <line x1="140" y1="40" x2="140" y2="340" stroke="#ef4444" strokeWidth="2" opacity="0.8" />
                        <line x1="920" y1="40" x2="920" y2="340" stroke="#ef4444" strokeWidth="2" opacity="0.8" />

                        <rect
                            x="135"
                            y="172"
                            width="10"
                            height="36"
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth="3"
                            rx="2"
                        />
                        <rect
                            x="915"
                            y="172"
                            width="10"
                            height="36"
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth="3"
                            rx="2"
                        />

                        <circle
                            cx="200"
                            cy="125"
                            r="45"
                            fill="none"
                            stroke="var(--color-chart-blue)"
                            strokeWidth="2"
                            opacity="0.5"
                        />
                        <circle
                            cx="200"
                            cy="255"
                            r="45"
                            fill="none"
                            stroke="var(--color-chart-blue)"
                            strokeWidth="2"
                            opacity="0.5"
                        />
                        <circle
                            cx="860"
                            cy="125"
                            r="45"
                            fill="none"
                            stroke="var(--color-chart-blue)"
                            strokeWidth="2"
                            opacity="0.5"
                        />
                        <circle
                            cx="860"
                            cy="255"
                            r="45"
                            fill="none"
                            stroke="var(--color-chart-blue)"
                            strokeWidth="2"
                            opacity="0.5"
                        />

                        <circle cx="200" cy="125" r="3" fill="#ef4444" />
                        <circle cx="200" cy="255" r="3" fill="#ef4444" />
                        <circle cx="860" cy="125" r="3" fill="#ef4444" />
                        <circle cx="860" cy="255" r="3" fill="#ef4444" />
                    </svg>
                    <canvas
                        ref={canvasRef}
                        className="absolute inset-0 opacity-95 z-10 cursor-crosshair w-full h-full"
                        width="1060"
                        height="380"
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    />
                </div>
            </div>

            <div className="flex flex-col items-center gap-1 sm:flex-row sm:justify-between mt-1 text-xs text-on-surface-variant">
                <div className="flex items-center gap-3 order-first sm:order-last shrink-0">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-red-400 rounded-full" />
                        <span className="whitespace-nowrap">Offensive KPI</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-blue-400 rounded-full" />
                        <span className="whitespace-nowrap">Defensive KPI</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-green-400 rounded-full" />
                        <span className="whitespace-nowrap">Team Play KPI</span>
                    </div>
                </div>
                <span className="text-[10px] italic opacity-60 whitespace-nowrap order-last sm:order-first">
                    Visualization based on shot position data - actual field dimensions may vary
                </span>
            </div>

            {hoveredShot && mousePosition && (
                <div
                    className="absolute z-50 pointer-events-none chart-tooltip"
                    style={{ left: mousePosition.x + 10, top: mousePosition.y - 10 }}
                >
                    <div className="flex flex-col gap-2">
                        <div className="font-semibold text-sm text-general-blue">
                            {hoveredShot.shooterId
                                ? playerLookup[hoveredShot.shooterId] || `Player ${hoveredShot.shooterId}`
                                : "Unknown"}{" "}
                            · {hoveredShot.period === 4 ? "Overtime" : `Period ${hoveredShot.period}`}
                        </div>

                        {hoveredShot.matchId && matchLookup[hoveredShot.matchId] && (
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1.5 text-xs text-on-surface">
                                    {getTeamLogo(matchLookup[hoveredShot.matchId].homeTeam) ? (
                                        <img
                                            src={getTeamLogo(matchLookup[hoveredShot.matchId].homeTeam)}
                                            className="h-5 w-5 object-contain"
                                            alt={matchLookup[hoveredShot.matchId].homeTeam}
                                        />
                                    ) : (
                                        <span className="font-medium">{matchLookup[hoveredShot.matchId].homeTeam}</span>
                                    )}
                                    <span className="font-bold">
                                        {matchLookup[hoveredShot.matchId].homeGoals}–
                                        {matchLookup[hoveredShot.matchId].awayGoals}
                                    </span>
                                    {getTeamLogo(matchLookup[hoveredShot.matchId].awayTeam) ? (
                                        <img
                                            src={getTeamLogo(matchLookup[hoveredShot.matchId].awayTeam)}
                                            className="h-5 w-5 object-contain"
                                            alt={matchLookup[hoveredShot.matchId].awayTeam}
                                        />
                                    ) : (
                                        <span className="font-medium">{matchLookup[hoveredShot.matchId].awayTeam}</span>
                                    )}
                                </div>
                                <div className="text-xs text-on-surface-variant mt-1">
                                    {matchLookup[hoveredShot.matchId].tournamentName} ·{" "}
                                    {dayjs(matchLookup[hoveredShot.matchId].date).format("MMM D, YYYY")}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                            <div
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{
                                    backgroundColor: hoveredShot.result.toLowerCase().includes("goal")
                                        ? "#EF4444"
                                        : hoveredShot.result.toLowerCase().includes("saved")
                                          ? "#3B82F6"
                                          : hoveredShot.result.toLowerCase().includes("blocked")
                                            ? "#F59E0B"
                                            : "#6B7280",
                                }}
                            />
                            <span className="text-xs text-on-surface">Result:</span>
                            <span
                                className="text-sm font-bold"
                                style={{
                                    color: hoveredShot.result.toLowerCase().includes("goal")
                                        ? "#EF4444"
                                        : hoveredShot.result.toLowerCase().includes("saved")
                                          ? "#3B82F6"
                                          : hoveredShot.result.toLowerCase().includes("blocked")
                                            ? "#F59E0B"
                                            : "#6B7280",
                                }}
                            >
                                {hoveredShot.result}
                            </span>
                        </div>

                        <div className="flex items-center gap-4 pt-1 border-t border-outline-variant/20 text-xs text-on-surface-variant">
                            <span>
                                <span className="font-medium text-on-surface">Time: </span>
                                {Math.floor((hoveredShot.secondsFromPeriodStart || 0) / 60)}:
                                {String((hoveredShot.secondsFromPeriodStart || 0) % 60).padStart(2, "0")}
                            </span>
                            <span>
                                <span className="font-medium text-on-surface">xG: </span>
                                {hoveredShot.expectedGoals?.toFixed(3) ?? "N/A"}
                            </span>
                            {hoveredShot.speed != null && (
                                <span>
                                    <span className="font-medium text-on-surface">Speed: </span>
                                    {hoveredShot.speed.toFixed(1)} km/h
                                </span>
                            )}
                        </div>

                        {(hoveredShot.fromLateralPass || hoveredShot.royalRoadCrossed) && (
                            <div className="flex gap-2 text-xs">
                                {hoveredShot.fromLateralPass && (
                                    <span className="px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400 font-medium">
                                        Lateral pass
                                    </span>
                                )}
                                {hoveredShot.royalRoadCrossed && (
                                    <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 font-medium">
                                        Royal road
                                    </span>
                                )}
                            </div>
                        )}

                        {hoveredShot.shooterId && playerKpiLookup[hoveredShot.shooterId] && (
                            <div className="flex flex-col gap-1 pt-1 border-t border-outline-variant/20">
                                <div className="text-[10px] font-headline font-black uppercase tracking-widest text-on-surface-variant">
                                    Player KPI
                                </div>
                                {[
                                    {
                                        label: "Offensive",
                                        color: "#F87171",
                                        value: playerKpiLookup[hoveredShot.shooterId].pointsOffensive,
                                    },
                                    {
                                        label: "Defensive",
                                        color: "#60A5FA",
                                        value: playerKpiLookup[hoveredShot.shooterId].pointsDefensive,
                                    },
                                    {
                                        label: "Team Play",
                                        color: "#4ADE80",
                                        value: playerKpiLookup[hoveredShot.shooterId].pointsTeamPlay,
                                    },
                                    {
                                        label: "Total",
                                        color: "#FACC15",
                                        value: playerKpiLookup[hoveredShot.shooterId].totalPoints,
                                    },
                                ].map(({ label, color, value }) => (
                                    <div key={label} className="flex items-center gap-2">
                                        <div
                                            className="w-2.5 h-2.5 rounded-full shrink-0"
                                            style={{ backgroundColor: color }}
                                        />
                                        <span className="text-xs text-on-surface-variant">{label}:</span>
                                        <span className="text-xs font-bold" style={{ color }}>
                                            {value.toFixed(1)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="h-15 lg:h-0" />
        </div>
    );
}
